import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { SortTh } from './RapidOptionsPanel';

/**
 * "Rapid Moves" tab of Manage Signals — today's quick STOCK bursts up and down
 * (RapidMoveService: >= 1% within a few minutes on >= 2x volume, $1B+ companies),
 * re-scanned every minute on the server. 🔔 = also sent as an admin alert.
 * Watch only — no signal is created from here.
 */

const SORT_VALUE = {
  detectedAt: (r) => String(r.detectedAt || ''),
  move: (r) => Math.abs(Number(r.movePct || 0)),
  rvol: (r) => Number(r.relVolume || 0),
  price: (r) => Number(r.price || 0),
  cap: (r) => Number(r.marketCapBillions || 0),
};

export default function RapidMovesPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('ALL');
  const [sort, setSort] = useState({ key: 'detectedAt', dir: -1 }); // newest first
  const [enabled, setEnabled] = useState(null);
  const [enabledLoading, setEnabledLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/stock/rapid-moves');
      setData(r.data || {});
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load rapid moves');
    }
  };

  useEffect(() => {
    api.get('/signals/rapid-move/enabled').then((r) => setEnabled(!!r.data?.enabled)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (checked) => {
    setEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/rapid-move/enabled?enabled=${checked}`);
      setEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setEnabledLoading(false);
    }
  };

  const up = (data?.up || []).map((r) => ({ ...r, dir: 'UP' }));
  const down = (data?.down || []).map((r) => ({ ...r, dir: 'DOWN' }));
  const rows = (view === 'UP' ? up : view === 'DOWN' ? down : [...up, ...down]);
  const val = SORT_VALUE[sort.key];
  rows.sort((a, b) => {
    const x = val(a), y = val(b);
    return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
  });

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Rapid Moves — quick stock bursts (watch only)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {data?.windowMinutes
                ? `${data.minMovePct}%+ in ${data.windowMinutes} min · ${data.minRelVolume}x+ volume · $${data.minMarketCapBillion}B+ companies · ${data.alertsToday ?? 0}/${data.maxAlertsPerDay} alerts sent today`
                : 'Quick moves up or down on heavy volume, re-scanned every minute.'}
              {' '}🔔 = also sent as an alert.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={!!enabled}
              disabled={enabled === null || enabledLoading}
              onChange={(e) => toggle(e.target.checked)}
            />
            Auto-scan
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {[
          { id: 'ALL', label: `All (${up.length + down.length})` },
          { id: 'UP', label: `▲ Up (${up.length})` },
          { id: 'DOWN', label: `▼ Down (${down.length})` },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setView(f.id)}
            className={`text-xs px-3 py-1.5 rounded-lg transition ${view === f.id ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-rose-600">{error}</div>}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <SortTh label="Detected" k="detectedAt" sort={sort} setSort={setSort} align="left" />
              <th className="text-left px-3 py-2 font-medium">Symbol</th>
              <SortTh label="Move" k="move" sort={sort} setSort={setSort} title="Size of the quick move (not the day's change)" />
              <SortTh label="Price" k="price" sort={sort} setSort={setSort} />
              <SortTh label="Rel. volume" k="rvol" sort={sort} setSort={setSort} title="Volume vs the same time in prior sessions" />
              <SortTh label="Mkt cap" k="cap" sort={sort} setSort={setSort} />
              <th className="text-left px-3 py-2 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  {!data ? 'Loading…' : data.message || 'No rapid moves yet today.'}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isUp = r.dir === 'UP';
              return (
                <tr key={`${r.dir}-${r.ticker}-${r.detectedAt}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.detectedAt}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.alerted && <span title="Sent as an alert">🔔 </span>}
                    <Link to={`/stock/${encodeURIComponent(r.ticker)}`} className="font-semibold text-slate-900 hover:underline">{r.ticker}</Link>
                    <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${isUp ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {isUp ? '▲ UP' : '▼ DOWN'}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap font-semibold ${isUp ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {Number(r.movePct) >= 0 ? '+' : ''}{Number(r.movePct).toFixed(2)}%
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">${Number(r.price).toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${Number(r.relVolume) >= 3 ? 'font-semibold text-sky-700' : 'text-slate-600'}`}>
                    {Number(r.relVolume).toFixed(1)}×
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-slate-600">
                    {r.marketCapBillions != null ? `$${Number(r.marketCapBillions).toFixed(0)}B` : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-400">{r.lastSeenAt}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        Watch feed only — a burst is a heads-up, not a trade: in backtests about half kept going after 30 minutes.
      </p>
    </div>
  );
}
